/**
 * Utility functions for managing application state using local/session storage.
 */
import type { SidebarItemState } from '@/store/slices/ui-state.slice'

const SIDEBAR_ITEMS_STATE_KEY = 'sidebarItemsState'

/**
 * Saves the current state of the sidebar items to local storage.
 *
 * @param sidebarItemsState The current state of the sidebar items to be saved
 */
export const saveSidebarItemsStateToLocalStorage = (sidebarItemsState: SidebarItemState[]) => {
  try {
    localStorage.setItem(SIDEBAR_ITEMS_STATE_KEY, JSON.stringify(sidebarItemsState))
  } catch (error) {
    console.error('Failed to save sidebar items state to localStorage:', error)
  }
}

/**
 * Loads the state of the sidebar items from local storage.
 *
 * @returns The parsed state from local storage, or `null` if nothing is found or parsing fails
 */
export const loadSidebarItemsStateFromLocalStorage = (): SidebarItemState[] | null => {
  try {
    const sidebarItemsStateFromLocalStorage = localStorage.getItem(SIDEBAR_ITEMS_STATE_KEY)
    return sidebarItemsStateFromLocalStorage ? JSON.parse(sidebarItemsStateFromLocalStorage) : null
  } catch (error) {
    console.error('Failed to parse sidebar items state from localStorage:', error)
    localStorage.removeItem(SIDEBAR_ITEMS_STATE_KEY)
    return null
  }
}

const SIDE_NAV_CONTRACTED_KEY = 'sideNavContracted'

export const saveSideNavContractedToLocalStorage = (contracted: boolean): void => {
  try {
    localStorage.setItem(SIDE_NAV_CONTRACTED_KEY, JSON.stringify(contracted))
  } catch (error) {
    console.error('Failed to save side nav contracted state to localStorage:', error)
  }
}

/** @returns the stored boolean, or `null` when nothing is stored or the stored value is unusable */
export const loadSideNavContractedFromLocalStorage = (): boolean | null => {
  try {
    const raw = localStorage.getItem(SIDE_NAV_CONTRACTED_KEY)
    if (raw === null) return null

    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'boolean' ? parsed : null
  } catch (error) {
    console.error('Failed to read side nav contracted state from localStorage:', error)
    return null
  }
}
